import { useEffect, useRef } from "react";
import { Opportunity } from "~/api/models/Opportunity";
import useInitializeBundles from "~/hooks/useInitializeBundles";
import useInitializeCosmeticProducts from "~/hooks/useInitializeCosmeticProducts";
import useInitializeHairProducts from "~/hooks/useInitializeHairProducts";
import useInitializeSkinProducts from "~/hooks/useInitializeSkinProducts";

export type UseInitializeProductsArgs = {
  opportunity?: Opportunity;
  loadingOpportunity?: boolean;
  onComplete?: () => void;
};

const useInitializeProducts = ({
  opportunity,
  loadingOpportunity,
}: UseInitializeProductsArgs) => {
  // Use refs to track completion without causing re-renders
  const bundlesCompleted = useRef(false);
  const hairCompleted = useRef(false);
  const cosmeticCompleted = useRef(false);
  const lastOpportunityId = useRef<string | null>(null);

  // Reset completion tracking when opportunity changes
  useEffect(() => {
    if (opportunity?.id !== lastOpportunityId.current) {
      bundlesCompleted.current = false;
      hairCompleted.current = false;
      cosmeticCompleted.current = false;
      lastOpportunityId.current = opportunity?.id || null;
    }
  }, [opportunity?.id]);

  // Step 1: Bundles (run first to prevent decomposition)
  useInitializeBundles({
    opportunity,
    loadingOpportunity,
    onComplete: () => {
      bundlesCompleted.current = true;
    },
  });

  // Step 2: Hair products (runs after bundles are handled)
  useInitializeHairProducts({
    opportunity,
    loadingOpportunity: loadingOpportunity || !bundlesCompleted.current,
    onComplete: () => {
      hairCompleted.current = true;
    },
  });

  // Step 3: Cosmetic products (runs after hair products are added)
  useInitializeCosmeticProducts({
    opportunity,
    loadingOpportunity: loadingOpportunity || !hairCompleted.current,
    onComplete: () => {
      cosmeticCompleted.current = true;
    },
  });

  // Step 4: Skin products (runs after cosmetic products are added)
  useInitializeSkinProducts({
    opportunity,
    loadingOpportunity: loadingOpportunity || !cosmeticCompleted.current,
  });
};

export default useInitializeProducts;
